import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PricingRules() {
  const [d, setD] = useState(null);
  const [mat, setMat] = useState({ density_kg_m3: "", internal_cost_eur_kg: "", discounted_scrap_share_percent: "", scrap_material_cost_factor_percent: "20" });
  const [mk, setMk] = useState({ default_markup_percent: "", reason: "" });
  const [open, setOpen] = useState(false);

  const load = () => api.get("/reader/pricing-rules").then((r) => {
    setD(r.data);
    const m = r.data.material_rule;
    if (m) setMat({ density_kg_m3: m.density_kg_m3 ?? "", internal_cost_eur_kg: m.internal_cost_eur_kg ?? "", discounted_scrap_share_percent: m.discounted_scrap_share_percent ?? "", scrap_material_cost_factor_percent: m.scrap_material_cost_factor_percent ?? "20" });
    const k = r.data.markup_rule;
    setMk((p) => ({ ...p, default_markup_percent: k?.default_markup_percent ?? r.data.recovered_markup_percent ?? "" }));
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;

  const saveMat = async () => {
    try {
      await api.put("/reader/material-rule", { grade: "S355", ...Object.fromEntries(Object.entries(mat).map(([k, v]) => [k, v === "" ? null : v])) });
      toast.success("Regola materiale salvata (evento MATERIAL_PRICING_RULE_CONFIGURED)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveMk = async () => {
    try {
      await api.put("/reader/markup-rule", mk);
      toast.success("Regola ricarico confermata (evento MARKUP_RULE_CONFIRMED)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const F = ({ label, k, ph }) => (
    <label className="block">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <Input type="number" min="0" step="0.01" className="h-7 text-xs" placeholder={ph} data-testid={`mat-${k}`}
        value={mat[k]} onChange={(e) => setMat({ ...mat, [k]: e.target.value })} />
    </label>
  );

  return (
    <div className="border border-border bg-white mb-4" data-testid="pricing-rules">
      <div className="px-3 py-2 flex items-center justify-between bg-muted cursor-pointer" onClick={() => setOpen(!open)} data-testid="pricing-rules-toggle">
        <div className="text-sm font-display font-bold">Profilo Officina — Regola materiale & ricarico ({d.formula_version})</div>
        <div className="text-xs">
          {d.material_rule?.internal_cost_eur_kg != null
            ? <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-900 font-bold" data-testid="material-rule-status">MATERIALE CONFIGURATO</span>
            : <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 font-bold" data-testid="material-rule-status">MATERIALE DA CONFIGURARE</span>}
          <span className="ml-2 text-muted-foreground">{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && (
        <div className="p-3 text-xs space-y-3">
          <p className="text-muted-foreground">{d.note}</p>
          <div className="border border-border p-2" data-testid="material-rule-box">
            <div className="font-bold mb-1">Regola materiale — metodo: Rettangolo pieno dello sviluppo (fori/asole/scantonature MAI sottratti)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
              <F label="Densità S355 kg/m³ (mai inserita silenziosamente)" k="density_kg_m3" ph="es. 7850" />
              <F label="Costo interno materia €/kg (INTERNAL_COST)" k="internal_cost_eur_kg" ph="—" />
              <F label="Quota sfrido a valore ridotto % (facoltativa, 0 se vuota)" k="discounted_scrap_share_percent" ph="0" />
              <F label="Valorizzazione sfrido sul costo materia % (default 20)" k="scrap_material_cost_factor_percent" ph="20" />
              <Button size="sm" onClick={saveMat} data-testid="save-material-rule">Salva regola materiale</Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1" title="La quota sfrido è già compresa nel peso del rettangolo e viene valorizzata a una percentuale ridotta del costo materia." data-testid="scrap-rule-tooltip">
              ⓘ La quota sfrido è già compresa nel peso del rettangolo e viene valorizzata a una percentuale ridotta del costo materia (mai aggiunta al peso).
            </div>
          </div>
          <div className="border border-border p-2" data-testid="markup-rule-box">
            <div className="font-bold mb-1">Regola ricarico — vendita = costo interno × (1 + ricarico%) · mai margine · applicato una sola volta</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
              <label className="block"><span className="text-[10px] text-muted-foreground">Ricarico aziendale predefinito % {d.markup_rule ? "(confermato)" : `(precompilato: ${d.recovered_markup_percent}% recuperato P5A)`}</span>
                <Input type="number" min="0" step="0.01" className="h-7 text-xs" data-testid="markup-percent"
                  value={mk.default_markup_percent} onChange={(e) => setMk({ ...mk, default_markup_percent: e.target.value })} /></label>
              <label className="block md:col-span-2"><span className="text-[10px] text-muted-foreground">Motivazione (obbligatoria, Owner/Admin)</span>
                <Input className="h-7 text-xs" data-testid="markup-reason"
                  value={mk.reason} onChange={(e) => setMk({ ...mk, reason: e.target.value })} /></label>
              <Button size="sm" onClick={saveMk} data-testid="save-markup-rule">Conferma regola ricarico</Button>
            </div>
            {d.markup_rule?.history?.length > 0 && (
              <div className="text-[10px] text-muted-foreground mt-1" data-testid="markup-history">
                Cronologia: {d.markup_rule.history.map((h, i) => <span key={i}>{h.default_markup_percent}% ({h.updated_by_name}, {String(h.updated_at).slice(0, 10)}) · </span>)}
              </div>
            )}
          </div>
          {d.events.length > 0 && (
            <div className="text-[10px] text-muted-foreground" data-testid="pricing-events">
              Eventi (append-only): {d.events.slice(0, 5).map((e) => `#${e.seq} ${e.event} · ${e.by_name} · ${String(e.at).slice(0, 19)}`).join(" | ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
